// Tenant / route-base resolution.
//
// Two ways the app is served, sharing this codebase:
//
//   * Web  — one host (e.g. ccportal.go.com.sa) serves many tenants. The tenant
//            is derived from the URL: the first path segment when `suffixDomain`
//            is set, otherwise the left-most hostname label. Routes are prefixed
//            with `/call-center` (or `/<tenant>` in suffixDomain mode).
//
//   * Desktop (Electron) — the app loads from a file:// / app:// URL that carries
//            no tenant, so the tenant is TYPED on the login form and persisted
//            here (see setTenant). This is the same for SaaS and on-prem builds;
//            they differ only in the `middlewareURL`/`ws` baked into config.json
//            (SaaS = one shared endpoint, on-prem = that customer's endpoint).
//            Routes have no tenant prefix and the app uses hash history.
//
// `isDesktop` is a build-time flag: set VUE_APP_TARGET=electron for desktop builds.
// When unset (the default web build) every function below reproduces the exact
// pre-existing behaviour.

import config from "../../public/config.json";

export const isDesktop = process.env.VUE_APP_TARGET === "electron";

// localStorage key for the tenant the desktop user entered at login.
const TENANT_KEY = "tenant";

// Web: resolve the tenant from the URL. Consolidates the near-identical copies
// that previously lived in App.vue / LoginPage.vue / ForgotPassword.vue / auth
// actions / router — using the most complete variant (the login-flow one),
// including the trailing-dot cleanup.
function tenantFromUrl() {
  let domainName = (window.location.pathname.split("/")[1] || "").replace(
    /(\/*)\.(\/*?)$/,
    "$1"
  );
  if (config.suffixDomain || config.admin === domainName) {
    return domainName;
  }
  const host = window.location.hostname;
  return host.substring(0, host.indexOf("."));
}

// Web: the router path prefix. `/call-center` normally, `/<tenant>` in
// suffixDomain mode. Matches the former baseURL()/getbaseURL implementations.
function routeBaseFromUrl() {
  let base = "/call-center";
  const domainName = window.location.pathname.split("/")[1];
  if (config.suffixDomain || config.admin === domainName) {
    base = "/" + domainName;
  }
  return base;
}

// The tenant identifier sent to the backend (as the `domain` header/param).
// Web: from the URL. Desktop: the value typed at login and persisted, so
// autoLogin (session restore on relaunch) can read it back.
export function getTenant() {
  if (isDesktop) {
    return localStorage.getItem(TENANT_KEY) || "";
  }
  return tenantFromUrl();
}

// Desktop only: persist the tenant entered on the login form.
export function setTenant(tenant) {
  const value = (tenant || "").trim();
  if (value) {
    localStorage.setItem(TENANT_KEY, value);
  }
}

// The router path prefix. Empty on desktop (routes served from the hash root).
export function getRouteBase() {
  return isDesktop ? "" : routeBaseFromUrl();
}
